import React, { useMemo, useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Table,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Button,
} from '@mui/material';
import { ExpandMore, ChevronRight, InfoOutlined, Close } from '@mui/icons-material';
import axiosInstance from './axiosInstance';

const filterParamValue = (value) => {
  if (Array.isArray(value)) {
    const values = value.filter((item) => item !== null && item !== undefined && String(item).trim() !== '');
    return values.length ? values.join(',') : undefined;
  }
  if (value === null || value === undefined || String(value).trim() === '') return undefined;
  return value;
};

const formatCurrency = (value) => {
  const number = Number(value);
  if (!Number.isFinite(number)) return '£0.00';
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(number);
};

const formatPercentValue = (value) => {
  const number = Number(value);
  if (!Number.isFinite(number)) return '0.00%';
  return `${number.toFixed(2)}%`;
};

const buildOptions = (values) => {
  const unique = Array.from(new Set(values.filter(Boolean)));
  unique.sort((a, b) => a.localeCompare(b));
  return unique.map((value) => ({ value, label: value }));
};

const normaliseOptions = (options) =>
  (options || []).map((entry) =>
    typeof entry === 'string' ? { value: entry, label: entry } : entry
  );

const buildLabelLookup = (options) =>
  normaliseOptions(options).reduce((map, option) => {
    map[option.value] = option.label;
    return map;
  }, {});

const toNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
};

const toCsvCell = (value) => {
  if (value === null || value === undefined) return '';
  const stringValue = String(value);
  if (/[",\n]/.test(stringValue)) {
    return `"${stringValue.replace(/"/g, '""')}"`;
  }
  return stringValue;
};

const InsurerBreakdownTab = ({
  baseFilters,
  nominalCodes = [],
  panelWidth,
  filters: externalFilters,
  onFiltersChange,
  onFilterOptionsChange,
}) => {
  const [rows, setRows] = useState([]);
  const [groupRows, setGroupRows] = useState([]);
  const [visibleColumns, setVisibleColumns] = useState([]);
  const [totals, setTotals] = useState(null);
  const [childCategories, setChildCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [expandedInsurers, setExpandedInsurers] = useState(new Set());
  const [expandedSources, setExpandedSources] = useState(new Set());
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [selectedRowDetail, setSelectedRowDetail] = useState(null);
  const [selectedMainRowKey, setSelectedMainRowKey] = useState('');
  const [selectedJobDetailKey, setSelectedJobDetailKey] = useState('');
  const [internalFilters, setInternalFilters] = useState({
    insurerName: '',
    vehicleManufacturer: '',
    driveable: '',
    jobType: '',
  });
  const filters = externalFilters || internalFilters;
  const setFilters = onFiltersChange || setInternalFilters;

  const [insurerOptions, setInsurerOptions] = useState([]);
  const [manufacturerOptions, setManufacturerOptions] = useState([]);
  const [driveableOptions, setDriveableOptions] = useState([]);
  const [jobTypeOptions, setJobTypeOptions] = useState([]);
  const [sourceCodeOptions, setSourceCodeOptions] = useState([]);

  const fallbackInsurers = useMemo(
    () => buildOptions(rows.map((row) => row.insurer_name)),
    [rows]
  );
  const fallbackManufacturers = useMemo(
    () => buildOptions(rows.map((row) => row.vehicle_manufacturer)),
    [rows]
  );
  const fallbackDriveable = useMemo(
    () => buildOptions(rows.map((row) => row.driveable)),
    [rows]
  );
  const fallbackJobTypes = useMemo(() => {
    const values = [];
    rows.forEach((row) => {
      const list = Array.isArray(row.job_types) ? row.job_types : [];
      list.forEach((value) => values.push(value));
    });
    return buildOptions(values);
  }, [rows]);

  const displayedInsurerOptions = insurerOptions.length ? insurerOptions : fallbackInsurers;
  const displayedManufacturerOptions = manufacturerOptions.length ? manufacturerOptions : fallbackManufacturers;
  const displayedDriveableOptions = driveableOptions.length ? driveableOptions : fallbackDriveable;
  const displayedJobTypeOptions = jobTypeOptions.length ? jobTypeOptions : fallbackJobTypes;
  const displayedSourceCodeOptions = sourceCodeOptions.length ? sourceCodeOptions : [];
  const insurerLabels = useMemo(
    () => buildLabelLookup(displayedInsurerOptions),
    [displayedInsurerOptions]
  );
  const sourceCodeLabels = useMemo(
    () => buildLabelLookup(displayedSourceCodeOptions),
    [displayedSourceCodeOptions]
  );
  const manufacturerLabels = useMemo(
    () => buildLabelLookup(displayedManufacturerOptions),
    [displayedManufacturerOptions]
  );
  const driveableLabels = useMemo(
    () => buildLabelLookup(displayedDriveableOptions),
    [displayedDriveableOptions]
  );

  useEffect(() => {
    let isMounted = true;
    const fetchOptions = async () => {
      try {
        const [insurersRes, manufacturersRes, driveableRes, jobTypesRes, sourceCodeRes] = await Promise.all([
          axiosInstance.get('/api/v1/insurer'),
          axiosInstance.get('/api/v1/vehicle/manufacturer'),
          axiosInstance.get('/api/v1/vehicle/driveable-status'),
          axiosInstance.get('/api/v1/job/type'),
          axiosInstance.get('/api/v1/insurer/source-code'),
        ]);
        if (!isMounted) return;
        setInsurerOptions(
          normaliseOptions(
            (insurersRes?.data?.data || []).map((entry) => ({
              value: entry.insurer_id,
              label: entry.name || entry.insurer_id,
            }))
          )
        );
        setManufacturerOptions(
          normaliseOptions(
            (manufacturersRes?.data?.data || []).map((entry) => ({
              value: entry.vehicle_manufacturer_id,
              label: entry.name || entry.vehicle_manufacturer_id,
            }))
          )
        );
        setDriveableOptions(
          normaliseOptions(
            (driveableRes?.data?.data || []).map((entry) => ({
              value: entry.driveable_status_id,
              label: entry.name || entry.driveable_status_id,
            }))
          )
        );
        setJobTypeOptions(
          normaliseOptions(
            (jobTypesRes?.data?.data || []).map((entry) => ({
              value: entry.job_type_id,
              label: entry.name || entry.job_type_id,
            }))
          )
        );
        setSourceCodeOptions(
          normaliseOptions(
            (sourceCodeRes?.data?.data || []).map((entry) => ({
              value: entry.source_code_id,
              label: entry.name || entry.source_code_id,
            }))
          )
        );
      } catch (err) {
        if (!isMounted) return;
        setInsurerOptions([]);
        setManufacturerOptions([]);
        setDriveableOptions([]);
        setJobTypeOptions([]);
        setSourceCodeOptions([]);
      }
    };
    fetchOptions();
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (!onFilterOptionsChange) return;
    onFilterOptionsChange({
      insurers: normaliseOptions(displayedInsurerOptions),
      manufacturers: normaliseOptions(displayedManufacturerOptions),
      driveables: normaliseOptions(displayedDriveableOptions),
      jobTypes: normaliseOptions(displayedJobTypeOptions),
      sourceCodes: normaliseOptions(displayedSourceCodeOptions),
    });
  }, [
    onFilterOptionsChange,
    displayedInsurerOptions,
    displayedManufacturerOptions,
    displayedDriveableOptions,
    displayedJobTypeOptions,
    displayedSourceCodeOptions,
  ]);

  useEffect(() => {
    let isMounted = true;
    const fetchData = async () => {
      setLoading(true);
      setError('');
      try {
        const params = {
          start_date: baseFilters.startDate,
          end_date: baseFilters.endDate,
          cost_by_address: baseFilters.costBy,
          region_id: filterParamValue(baseFilters.regionId),
          location_id: filterParamValue(baseFilters.locationId),
          supplier_id: filterParamValue(baseFilters.supplierId),
          nominal_code_id: filterParamValue(baseFilters.nominalCodeId),
          insurer_name: filters.insurerName || undefined,
          vehicle_manufacturer: filters.vehicleManufacturer || undefined,
          driveable: filters.driveable || undefined,
          job_type: filters.jobType || undefined,
        };
        const response = await axiosInstance.get('/api/v1/insurer-breakdown', { params });
        const payload = response?.data?.data || {};
        if (!isMounted) return;
        setRows(payload.rows || []);
        setGroupRows(payload.group_rows || []);
        setTotals(payload.totals || null);
        setChildCategories(payload.child_categories || []);
        setVisibleColumns(payload.visible_columns || []);
        setHasLoadedOnce(true);
      } catch (err) {
        if (!isMounted) return;
        setError('Failed to load insurer breakdown data.');
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    fetchData();
    return () => {
      isMounted = false;
    };
  }, [baseFilters, filters]);

  useEffect(() => {
    if (!groupRows.length) return;
    setExpandedInsurers(new Set());
    setExpandedSources(new Set());
  }, [groupRows]);

  const columns = useMemo(() => {
    const selectedNominalValues = Array.isArray(baseFilters.nominalCodeId)
      ? baseFilters.nominalCodeId.filter(Boolean)
      : baseFilters.nominalCodeId
        ? [baseFilters.nominalCodeId]
        : [];
    const selectedNominalName = selectedNominalValues.length === 1
      ? nominalCodes.find(
        (nominal) => nominal?.nominal_code_id === selectedNominalValues[0]
      )?.name
      : null;
    const childColumns = (childCategories || []).map((child) => ({
      type: 'amount',
      key: `${child.key}_spend`,
      label: `${child.label} (£)`,
    }));
    const metricColumns = [
      { type: 'percent', key: 'car_parts_spend_percent', label: 'Car Parts Cost (%)' },
      { type: 'amount', key: 'car_parts_spend', label: 'Car Parts Cost (£)' },
      { type: 'amount', key: 'car_parts_sales', label: 'Car Parts Sales (£)' },
      { type: 'percent', key: 'car_parts_profit_percent', label: 'Car Parts Profit (%)' },
      { type: 'amount', key: 'genuine_parts_spend', label: 'Genuine (£)' },
      { type: 'amount', key: 'non_genuine_parts_spend', label: 'Non Genuine (£)' },
      { type: 'amount', key: 'green_parts_spend', label: 'Green (£)' },
      { type: 'percent', key: 'subcontracted_spend_percent', label: 'Subcontracted Cost (%)' },
      { type: 'amount', key: 'subcontracted_spend', label: 'Subcontracted Cost (£)' },
      { type: 'amount', key: 'subcontracted_sale', label: 'Subcontracted Sales (£)' },
      {
        type: 'percent',
        key: 'subcontracted_profit_percent',
        label: 'Subcontracted Profit (%)',
        tooltip: 'Calculated using Subcontracted Sales against displayed Subcontracted Cost, including ADAS.',
      },
      ...childColumns,
      { type: 'percent', key: 'storage_recovery_spend_percent', label: 'Storage & Recovery Cost (%)' },
      { type: 'amount', key: 'storage_recovery_spend', label: 'Storage & Recovery Cost (£)' },
      { type: 'amount', key: 'storage_and_recovery_sale', label: 'Storage & Recovery Sales (£)' },
      { type: 'percent', key: 'storage_recovery_profit_percent', label: 'Storage & Recovery Profit (%)' },
      { type: 'amount', key: 'labour_sales', label: 'Labor Sales (£)' },
      { type: 'number', key: 'hours_sold', label: 'Hours Sold' },
      { type: 'amount', key: 'paint_cost', label: 'Paint Cost (£)' },
      { type: 'amount', key: 'paint_sales', label: 'Paint Sales (£)' },
      { type: 'percent', key: 'paint_profit_percent', label: 'Paint Profit (%)' },
      { type: 'percent', key: 'total_spend_percent', label: 'Total Spend (%)' },
      { type: 'amount', key: 'total_spend', label: 'Total Spend (£)' },
    ];

    const filteredMetricColumns = (() => {
      if (Array.isArray(visibleColumns) && visibleColumns.length) {
        const filterSet = new Set([...visibleColumns, 'total_spend']);
        const relatedColumnMap = {
          car_parts_spend: ['car_parts_spend_percent', 'car_parts_spend', 'car_parts_sales', 'car_parts_profit_percent'],
          subcontracted_spend: ['subcontracted_spend_percent', 'subcontracted_spend', 'subcontracted_sale', 'subcontracted_profit_percent'],
          storage_recovery_spend: ['storage_recovery_spend_percent', 'storage_recovery_spend', 'storage_and_recovery_sale', 'storage_recovery_profit_percent'],
          total_spend: ['total_spend_percent', 'total_spend'],
        };
        const expandedKeys = new Set();
        filterSet.forEach((key) => {
          (relatedColumnMap[key] || [key]).forEach((relatedKey) => expandedKeys.add(relatedKey));
        });
        const filtered = metricColumns.filter((column) => expandedKeys.has(column.key));
        if (selectedNominalName && visibleColumns.length === 1) {
          return filtered.map((column) => {
            if (column.key === visibleColumns[0]) {
              return { ...column, label: `${selectedNominalName} (£)` };
            }
            return column;
          });
        }
        return filtered;
      }
      return metricColumns;
    })();

    return filteredMetricColumns;
  }, [childCategories, visibleColumns, nominalCodes, baseFilters.nominalCodeId]);

  const isPercentColumn = (key) => key.endsWith('_percent');
  const isPlainNumberColumn = (key) => key === 'hours_sold';
  const isProfitColumn = (key) => key.endsWith('_profit_percent');

  const getProfitValueColor = (value) => {
    const number = toNumber(value);
    if (number > 0) return '#1b5e20';
    if (number < 0) return '#b71c1c';
    return '#6b7280';
  };

  const getColumnCellSx = (column, value, isSummaryRow = false) => {
    if (!isProfitColumn(column.key)) {
      return isSummaryRow ? { fontWeight: 700 } : undefined;
    }
    return {
      bgcolor: isSummaryRow ? '#ecfdf3' : '#f7fcf8',
      color: getProfitValueColor(value),
      fontWeight: isSummaryRow ? 700 : 600,
    };
  };

  const formatColumnValue = (key, value) =>
    isPercentColumn(key)
      ? formatPercentValue(value)
      : isPlainNumberColumn(key)
        ? toNumber(value).toFixed(2)
        : formatCurrency(value);

  const displayRows = useMemo(() => {
    const insurerLabels = buildLabelLookup(displayedInsurerOptions);
    const sourceLabels = buildLabelLookup(displayedSourceCodeOptions);
    if (groupRows.length) {
      const rendered = [];
      let currentInsurer = null;
      let currentSource = null;
      let insurerExpanded = true;
      let sourceExpanded = true;
      groupRows.forEach((entry) => {
        if (entry.type === 'insurer') {
          currentInsurer = entry.insurer_name || 'unknown';
          currentSource = null;
          insurerExpanded = expandedInsurers.has(currentInsurer);
          rendered.push({
            type: 'group',
            level: 'insurer',
            label: insurerLabels[currentInsurer] || currentInsurer || 'unknown',
            totals: entry.totals,
            isExpanded: insurerExpanded,
            key: currentInsurer,
          });
          return;
        }
        if (entry.type === 'source') {
          currentSource = entry.source_code || 'unknown';
          const sourceKey = `${currentInsurer || 'unknown'}__${currentSource}`;
          sourceExpanded = expandedSources.has(sourceKey);
          if (insurerExpanded) {
            rendered.push({
              type: 'group',
              level: 'source',
              label: sourceLabels[currentSource] || currentSource || 'unknown',
              totals: entry.totals,
              isExpanded: sourceExpanded,
              key: sourceKey,
            });
          }
          return;
        }
        if (insurerExpanded && sourceExpanded) {
          rendered.push({ type: 'row', row: entry.row });
        }
      });
      return rendered;
    }
        const amountKeys = columns
          .map((column) => column.key)
          .filter((key) => !isPercentColumn(key));
        const sorted = [...rows].sort((a, b) => {
      const insurerCompare = (a.insurer_name || '').localeCompare(b.insurer_name || '');
      if (insurerCompare !== 0) return insurerCompare;
      const sourceCompare = (a.source_code || '').localeCompare(b.source_code || '');
      if (sourceCompare !== 0) return sourceCompare;
      const manufacturerCompare = (a.vehicle_manufacturer || '').localeCompare(b.vehicle_manufacturer || '');
      if (manufacturerCompare !== 0) return manufacturerCompare;
      return (a.driveable || '').localeCompare(b.driveable || '');
    });

    const groups = new Map();
    sorted.forEach((row) => {
      const insurer = row.insurer_name || 'unknown';
      const source = row.source_code || 'unknown';
      if (!groups.has(insurer)) {
        groups.set(insurer, new Map());
      }
      const sourceMap = groups.get(insurer);
      if (!sourceMap.has(source)) {
        sourceMap.set(source, []);
      }
      sourceMap.get(source).push(row);
    });

    const result = [];
    Array.from(groups.keys()).forEach((insurer) => {
      const sourceMap = groups.get(insurer);
      const insurerTotals = amountKeys.reduce((acc, key) => ({ ...acc, [key]: 0 }), {});
      Array.from(sourceMap.values()).forEach((sourceRows) => {
        sourceRows.forEach((row) => {
          amountKeys.forEach((key) => {
            insurerTotals[key] += toNumber(row[key]);
          });
        });
      });
      result.push({
        type: 'group',
        level: 'insurer',
        label: insurerLabels[insurer] || insurer,
        totals: insurerTotals,
      });

      Array.from(sourceMap.keys()).forEach((source) => {
        const sourceRows = sourceMap.get(source) || [];
        const sourceTotals = amountKeys.reduce((acc, key) => ({ ...acc, [key]: 0 }), {});
        sourceRows.forEach((row) => {
          amountKeys.forEach((key) => {
            sourceTotals[key] += toNumber(row[key]);
          });
        });
        result.push({
          type: 'group',
          level: 'source',
          label: sourceLabels[source] || source,
          totals: sourceTotals,
        });
        sourceRows.forEach((row) => {
          result.push({
            type: 'row',
            row,
            insurerLabel: insurerLabels[insurer] || insurer,
          });
        });
      });
    });
    return result;
  }, [
    rows,
    groupRows,
    displayedInsurerOptions,
    displayedSourceCodeOptions,
    columns,
    expandedInsurers,
    expandedSources,
  ]);

  const toggleInsurer = (key) => {
    setExpandedInsurers((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const toggleSource = (key) => {
    setExpandedSources((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const jobLevelExportRows = useMemo(() =>
    rows.flatMap((row) =>
      (row.job_breakdown || []).map((job) => ({
        insurer_name: row.insurer_name || 'unknown',
        source_code: row.source_code || 'unknown',
        vehicle_manufacturer: row.vehicle_manufacturer || 'unknown',
        driveable: row.driveable || 'unknown',
        ...job,
      }))
    ),
  [rows]);

  const detailColumns = useMemo(() => {
    const keys = [
      'job_number',
      'car_parts_spend',
      'car_parts_sales',
      'car_parts_profit_percent',
      'genuine_parts_spend',
      'non_genuine_parts_spend',
      'green_parts_spend',
      'subcontracted_spend',
      'subcontracted_sale',
      'subcontracted_profit_percent',
      'labour_sales',
      'hours_sold',
      'paint_cost',
      'paint_sales',
      'paint_profit_percent',
      ...childCategories.map((child) => `${child.key}_spend`),
      'storage_recovery_spend',
      'storage_and_recovery_sale',
      'storage_recovery_profit_percent',
      'total_spend',
    ];
    return [
      { key: 'job_number', label: 'Job Number', type: 'text' },
      ...columns.filter((column) => keys.includes(column.key)),
    ];
  }, [columns, childCategories]);

  const getMainRowKey = (row) =>
    `${row.insurer_name || 'unknown'}__${row.source_code || 'unknown'}__${row.vehicle_manufacturer || 'unknown'}__${row.driveable || 'unknown'}`;

  const getSelectedRowCellSx = (selected) =>
    selected
      ? {
          bgcolor: '#dbeafe !important',
          fontWeight: 600,
        }
      : undefined;

  const getSelectedJobCellSx = (selected, baseSx = {}) =>
    selected
      ? {
          ...baseSx,
          bgcolor: '#dbeafe !important',
          fontWeight: 600,
        }
      : baseSx;

  const panelPaperSx = panelWidth
    ? { width: panelWidth, maxWidth: panelWidth, ml: 'auto', mr: 0, boxSizing: 'border-box' }
    : { width: '100%' };

  const getInsurerLabel = (value) => insurerLabels[value] || value;
  const getSourceCodeLabel = (value) => sourceCodeLabels[value] || value;
  const getManufacturerLabel = (value) => manufacturerLabels[value] || value;
  const getDriveableLabel = (value) =>
    value === 'driveable' ? 'Yes' : value === 'not_driveable' ? 'No' : driveableLabels[value] || value;
  const getRoundedDecimalValue = (value) => {
    const number = Number(value);
    return Number.isFinite(number) ? number.toFixed(2) : value;
  };

  const handleExportCsv = () => {
    if (!jobLevelExportRows.length) return;

    const exportColumns = [
      { key: 'insurer_name', label: 'Insurer' },
      { key: 'source_code', label: 'Source Code' },
      { key: 'vehicle_manufacturer', label: 'Manufacturer' },
      { key: 'driveable', label: 'Driveable' },
      ...detailColumns.map((column) => ({
        key: column.key,
        label: column.label,
        type: column.type,
      })),
    ];

    const csvLines = [
      exportColumns.map((column) => toCsvCell(column.label)).join(','),
      ...jobLevelExportRows.map((row) =>
        exportColumns
          .map((column) => {
            const value = row[column.key];
            if (column.key === 'insurer_name') {
              return toCsvCell(getInsurerLabel(value));
            }
            if (column.key === 'source_code') {
              return toCsvCell(getSourceCodeLabel(value));
            }
            if (column.key === 'vehicle_manufacturer') {
              return toCsvCell(getManufacturerLabel(value));
            }
            if (column.key === 'driveable') {
              return toCsvCell(getDriveableLabel(value));
            }
            if (column.type && column.type !== 'text') {
              return toCsvCell(getRoundedDecimalValue(value));
            }
            return toCsvCell(value);
          })
          .join(',')
      ),
    ];

    const blob = new Blob([csvLines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `insurer_breakdown_jobs_${baseFilters.startDate}_${baseFilters.endDate}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const showInitialSpinner = loading && !hasLoadedOnce;
  const showOverlay = loading && hasLoadedOnce;

  return (
    <Box sx={{ mt: 3, width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
      {showInitialSpinner ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3, ...panelPaperSx }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Paper sx={{ p: 2, ...panelPaperSx }}>
          <Typography color="error">{error}</Typography>
        </Paper>
      ) : (
        <Paper
          sx={{
            p: { xs: 2, md: 3 },
            ...panelPaperSx,
            position: 'relative',
          }}
        >
          {showOverlay && (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                bgcolor: 'rgba(255,255,255,0.65)',
                zIndex: 2,
              }}
            />
          )}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Insurer Breakdown
            </Typography>
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={handleExportCsv}
              disabled={!jobLevelExportRows.length || loading}
            >
              Download CSV
            </Button>
          </Box>
          <TableContainer sx={{ maxHeight: '65vh', overflowX: 'auto', overflowY: 'auto' }}>
            <Table size="small" stickyHeader sx={{ minWidth: 1200 }}>
              <TableHead>
                <TableRow>
                <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Insurer / Source</TableCell>
                <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Manufacturer</TableCell>
                <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Driveable</TableCell>
                {columns.map((column) => (
                  <TableCell
                    key={column.key}
                    sx={{
                      fontWeight: 600,
                      whiteSpace: 'nowrap',
                      ...(isProfitColumn(column.key)
                        ? {
                            bgcolor: '#e6f4ea',
                            color: '#1f4d2e',
                          }
                        : {}),
                    }}
                    align="right"
                  >
                    <Box
                      component="span"
                      sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
                    >
                      <Box component="span">{column.label}</Box>
                      {column.tooltip ? (
                        <Tooltip title={column.tooltip} arrow>
                          <InfoOutlined sx={{ fontSize: 16, opacity: 0.8, verticalAlign: 'middle' }} />
                        </Tooltip>
                      ) : null}
                    </Box>
                  </TableCell>
                ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {displayRows.map((entry, index) => {
                  if (entry.type === 'group') {
                    const CaretIcon = entry.isExpanded ? ExpandMore : ChevronRight;
                    const groupSelectionKey = `group:${entry.level}:${entry.key}`;
                    const isSelected = selectedMainRowKey === groupSelectionKey;
                    return (
                      <TableRow
                        key={`${entry.level}-${entry.label}-${index}`}
                        sx={{
                          bgcolor: isSelected
                            ? '#dbeafe'
                            : entry.level === 'insurer'
                              ? '#eef2f7'
                              : '#f8fafc',
                          cursor: 'pointer',
                          boxShadow: isSelected ? 'inset 4px 0 0 #2563eb' : 'none',
                        }}
                        onClick={() => {
                          setSelectedMainRowKey(groupSelectionKey);
                          setSelectedRowDetail(null);
                          setSelectedJobDetailKey('');
                          if (entry.level === 'insurer') {
                            toggleInsurer(entry.key);
                          } else {
                            toggleSource(entry.key);
                          }
                        }}
                      >
                        <TableCell
                          sx={{
                            pl: entry.level === 'source' ? 4 : 2,
                            whiteSpace: 'nowrap',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            fontWeight: isSelected ? 700 : 400,
                          }}
                        >
                          <CaretIcon fontSize="small" />
                          {entry.level === 'insurer' ? `Insurer: ${entry.label}` : `Source Code: ${entry.label}`}
                        </TableCell>
                        <TableCell />
                        <TableCell />
                        {columns.map((column) => (
                          <TableCell
                            key={column.key}
                            align="right"
                            sx={{
                              ...getColumnCellSx(column, entry.totals?.[column.key]),
                              ...(isSelected
                                ? {
                                    bgcolor: '#dbeafe !important',
                                    fontWeight: 700,
                                  }
                                : {}),
                            }}
                          >
                            {formatColumnValue(column.key, entry.totals?.[column.key])}
                          </TableCell>
                        ))}
                      </TableRow>
                    );
                  }
                  const row = entry.row;
                  return (
                    (() => {
                      const isSelected = selectedMainRowKey === getMainRowKey(row);
                      return (
                    <TableRow
                      key={`${row.insurer_name}-${row.source_code}-${row.vehicle_manufacturer}-${row.driveable}-${index}`}
                      sx={{
                        bgcolor: isSelected ? '#dbeafe' : index % 2 ? '#fcfcfd' : 'transparent',
                        cursor: row.job_count ? 'pointer' : 'default',
                        boxShadow: isSelected ? 'inset 4px 0 0 #2563eb' : 'none',
                      }}
                      onClick={() => {
                        if (row.job_count) {
                          setSelectedMainRowKey(getMainRowKey(row));
                          setSelectedRowDetail(row);
                          setSelectedJobDetailKey('');
                        }
                      }}
                    >
                      <TableCell sx={getSelectedRowCellSx(isSelected)}>
                        {row.job_count ? (
                          <Box component="span" sx={{ color: '#2563eb', fontWeight: 600 }}>
                            View {row.job_count} jobs
                          </Box>
                        ) : null}
                      </TableCell>
                      <TableCell sx={getSelectedRowCellSx(isSelected)}>
                        {getManufacturerLabel(row.vehicle_manufacturer)}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap', ...getSelectedRowCellSx(isSelected) }}>{getDriveableLabel(row.driveable)}</TableCell>
                      {columns.map((column) => (
                        <TableCell
                          key={column.key}
                          align="right"
                          sx={{
                            ...getColumnCellSx(column, row[column.key]),
                            ...getSelectedRowCellSx(isSelected),
                          }}
                        >
                          {formatColumnValue(column.key, row[column.key])}
                        </TableCell>
                      ))}
                    </TableRow>
                      );
                    })()
                  );
                })}
                {totals ? (
                  <TableRow
                    sx={{
                      position: 'sticky',
                      bottom: 0,
                      bgcolor: '#ffffff',
                      boxShadow: '0 -2px 4px rgba(0,0,0,0.06)',
                    }}
                  >
                    <TableCell sx={{ fontWeight: 700 }}>Total</TableCell>
                    <TableCell />
                    <TableCell />
                    {columns.map((column) => (
                      <TableCell
                        key={column.key}
                        sx={getColumnCellSx(column, totals[column.key], true)}
                        align="right"
                      >
                        {formatColumnValue(column.key, totals[column.key])}
                      </TableCell>
                    ))}
                  </TableRow>
                ) : null}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
      <Dialog
        open={Boolean(selectedRowDetail)}
        onClose={() => {
          setSelectedRowDetail(null);
          setSelectedJobDetailKey('');
        }}
        fullWidth
        maxWidth="lg"
      >
        <DialogTitle sx={{ pr: 6 }}>
          Job Breakdown
          {selectedRowDetail ? (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {`${getInsurerLabel(selectedRowDetail.insurer_name) || 'unknown'} / ${
                getSourceCodeLabel(selectedRowDetail.source_code) || 'unknown'
              } / ${getManufacturerLabel(selectedRowDetail.vehicle_manufacturer) || 'unknown'} / ${
                getDriveableLabel(selectedRowDetail.driveable) || 'unknown'
              }`}
            </Typography>
          ) : null}
          <IconButton
            aria-label="close"
            onClick={() => setSelectedRowDetail(null)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          <TableContainer sx={{ maxHeight: '65vh', pb: 4 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {detailColumns.map((column) => (
                    <TableCell
                      key={column.key}
                      align={column.key === 'job_number' ? 'left' : 'right'}
                      sx={{
                        fontWeight: 600,
                        whiteSpace: 'nowrap',
                        ...(isProfitColumn(column.key)
                          ? {
                              bgcolor: '#e6f4ea',
                              color: '#1f4d2e',
                          }
                        : {}),
                      }}
                    >
                      <Box
                        component="span"
                        sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
                      >
                        <Box component="span">{column.label}</Box>
                        {column.tooltip ? (
                          <Tooltip title={column.tooltip} arrow>
                            <InfoOutlined sx={{ fontSize: 16, opacity: 0.8, verticalAlign: 'middle' }} />
                          </Tooltip>
                        ) : null}
                      </Box>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {(selectedRowDetail?.job_breakdown || []).map((job) => (
                  (() => {
                    const isSelected = selectedJobDetailKey === job.job_number;
                    return (
                      <TableRow
                        key={job.job_number}
                        hover
                        sx={{
                          cursor: 'pointer',
                          bgcolor: isSelected ? '#dbeafe' : 'transparent',
                          boxShadow: isSelected ? 'inset 4px 0 0 #2563eb' : 'none',
                        }}
                        onClick={() => setSelectedJobDetailKey(job.job_number)}
                      >
                        {detailColumns.map((column) => (
                          <TableCell
                            key={column.key}
                            align={column.key === 'job_number' ? 'left' : 'right'}
                            sx={
                              column.key === 'job_number'
                                ? getSelectedJobCellSx(isSelected, { fontWeight: 600, whiteSpace: 'nowrap' })
                                : getSelectedJobCellSx(isSelected, getColumnCellSx(column, job[column.key]))
                            }
                          >
                            {column.key === 'job_number' ? job.job_number : formatColumnValue(column.key, job[column.key])}
                          </TableCell>
                        ))}
                      </TableRow>
                    );
                  })()
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default InsurerBreakdownTab;
